import {
  useEffect,
  useState,
} from "react";
import {
  Navigate,
  useLocation,
} from "react-router-dom";
import { useAuth } from "../../AuthContext";
import {
  canUseLearnerPortal,
  getMyDualPortalTestAccess,
  getMyPortalAccess,
  resolveDestinationFromAccess,
} from "../../lib/access/portalAccess";

export default function RequireLearner({
  children,
}) {
  const {
    user,
    loading: authLoading,
  } = useAuth();

  const location = useLocation();

  const [
    checking,
    setChecking,
  ] = useState(true);

  const [
    allowed,
    setAllowed,
  ] = useState(false);

  const [
    redirectTo,
    setRedirectTo,
  ] = useState("");

  useEffect(() => {
    let alive = true;

    async function checkAccess() {
      if (authLoading) return;

      if (!user?.id) {
        if (alive) {
          setAllowed(false);
          setRedirectTo("");
          setChecking(false);
        }

        return;
      }

      try {
        setChecking(true);

        const access =
          await getMyPortalAccess();

        if (!alive) return;

        /*
         * Ordinary learner accounts retain
         * their existing access without an
         * additional tester lookup.
         */
        if (
          access?.account_type ===
          "learner"
        ) {
          setAllowed(true);
          setRedirectTo("");
          setChecking(false);

          return;
        }

        /*
         * Partner and admin accounts only gain
         * learner-route access when they are in
         * the explicit dual tester allowlist.
         */
        const dualPortalAccess =
          await getMyDualPortalTestAccess();

        if (!alive) return;

        if (
          canUseLearnerPortal({
            access,
            dualPortalAccess,
          })
        ) {
          setAllowed(true);
          setRedirectTo("");
          setChecking(false);

          return;
        }

        /*
         * Non-test partner and admin accounts
         * continue to their existing portal.
         */
        const result =
          await resolveDestinationFromAccess({
            access,
            userId: user.id,
          });

        if (!alive) return;

        setAllowed(false);

        setRedirectTo(
          result?.destination ||
            "/login"
        );

        setChecking(false);
      } catch (error) {
        console.error(
          "Learner access check failed:",
          error
        );

        if (alive) {
          setAllowed(false);
          setRedirectTo("/login");
          setChecking(false);
        }
      }
    }

    checkAccess();

    return () => {
      alive = false;
    };
  }, [
    user?.id,
    authLoading,
  ]);

  if (
    authLoading ||
    checking
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 text-sm text-gray-600">
        Checking account access…
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: location,
        }}
      />
    );
  }

  if (!allowed) {
    return (
      <Navigate
        to={
          redirectTo ||
          "/login"
        }
        replace
      />
    );
  }

  return children;
}